// grid.js — renders the three-page swipeable button grid.
//
// Pure DOM rendering: no animations, no sound playback. Those come
// in Phases 4 and 5. Phase 3 wires up the structure and a callback
// so later phases can plug in.
//
// Tap handling: we deliberately do NOT use the browser's native click
// event here. iOS Safari's click synthesis is unreliable for buttons
// inside a horizontal scroll-snap container — the scroll gesture
// recognizer on the `.grid` container swallows touches before they
// can become clicks on children, so taps register as "nothing
// happened" on device even though they work perfectly on desktop.
//
// Instead we listen to pointerdown/pointerup ourselves with a
// generous movement tolerance: if the finger came up near where it
// went down and not too long after, we count it as a tap. This also
// gives us the freedom to be more lenient than iOS's native scroll/
// tap disambiguation, which is stingy enough that any finger jitter
// turns a tap into a (nonexistent) scroll. Keyboard activation
// (Enter/Space on a focused button) is handled via keydown so
// accessibility is preserved.

import { getCategories } from './data/catalog.js';
import { t } from './i18n.js';

// Tap tolerance thresholds. A child may press imprecisely, so we
// allow a bit of movement and a slightly long hold before giving up
// on the tap. These are generous compared to iOS native.
const TAP_MAX_MOVE_PX = 12;
const TAP_MAX_DURATION_MS = 800;

/**
 * @typedef {Object} GridHandles
 * @property {HTMLElement} container - The scrollable pages container.
 * @property {HTMLElement[]} pages   - One element per category page.
 * @property {HTMLButtonElement[]} buttons - All item buttons, in order.
 * @property {number} pageCount
 */

/**
 * Build the grid into `parent`. Returns handles for the page navigator.
 *
 * @param {HTMLElement} parent
 * @param {{ onItemTap: (itemId: string, button: HTMLButtonElement) => void }} opts
 * @returns {GridHandles}
 */
export function renderGrid(parent, { onItemTap }) {
  if (typeof onItemTap !== 'function') {
    throw new TypeError('renderGrid: opts.onItemTap must be a function');
  }

  const container = document.createElement('div');
  container.className = 'grid';
  container.setAttribute('role', 'region');
  container.setAttribute('aria-label', t('app.title'));

  const categories = getCategories();
  const pages = [];
  const buttons = [];

  for (const category of categories) {
    const page = document.createElement('section');
    page.className = 'grid__page';
    page.dataset.categoryId = category.id;
    page.setAttribute('aria-label', t(category.labelKey));
    page.style.backgroundImage = `url("${category.background}")`;

    // Visually-hidden page heading, for screen readers and tests.
    const heading = document.createElement('h2');
    heading.className = 'grid__page-heading';
    heading.textContent = t(category.labelKey);
    page.appendChild(heading);

    const buttonGrid = document.createElement('div');
    buttonGrid.className = 'grid__buttons';
    page.appendChild(buttonGrid);

    for (const item of category.items) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'grid__button';
      btn.dataset.itemId = item.id;
      btn.setAttribute('aria-label', t(item.labelKey));

      const img = document.createElement('img');
      img.className = 'grid__button-img';
      img.src = item.imagePath;
      img.alt = '';
      // Buttons off-screen (pages 2, 3) can defer loading.
      img.loading = 'lazy';
      img.decoding = 'async';
      // Eagerly preload the first page so it looks instant.
      if (category === categories[0]) {
        img.loading = 'eager';
        img.fetchPriority = 'high';
      }
      btn.appendChild(img);

      attachTapHandler(btn, () => onItemTap(item.id, btn));

      buttonGrid.appendChild(btn);
      buttons.push(btn);
    }

    container.appendChild(page);
    pages.push(page);
  }

  parent.appendChild(container);

  return {
    container,
    pages,
    buttons,
    pageCount: pages.length,
  };
}

/**
 * Attach a tap handler to `btn` that fires `fire()` on any of:
 *   - a pointerdown → pointerup sequence where the pointer stayed
 *     close to its start (pointer events, used by modern touch and
 *     mouse on every browser)
 *   - a `click` event whose target came from a non-pointer source,
 *     used as a defensive fallback — we dedupe against pointer taps
 *     so a single touch can never fire the handler twice
 *   - a keyboard activation (Enter/Space on the focused button)
 *
 * The pointer path is what makes this work on iOS Safari: listening
 * to pointerdown on the button "claims" the element for tap
 * handling, so the scroll container doesn't swallow the touch.
 */
function attachTapHandler(btn, fire) {
  /** @type {{ id: number, x: number, y: number, t: number } | null} */
  let active = null;
  // A small cooldown so a native click event (if iOS ever decides to
  // fire one) doesn't re-trigger the handler right after a pointerup
  // tap. 300ms covers the legacy iOS synthesized-click delay.
  let lastFireAt = 0;
  const firedRecently = () => Date.now() - lastFireAt < 400;

  const tryFire = () => {
    if (firedRecently()) return;
    lastFireAt = Date.now();
    fire();
  };

  btn.addEventListener('pointerdown', (event) => {
    // Primary button / primary touch only.
    if (event.button !== undefined && event.button > 0) return;
    active = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      t: Date.now(),
    };
    // Note: we intentionally do NOT call preventDefault here — the
    // scroll container above us needs to be able to take over the
    // gesture if the user is actually trying to swipe between pages.
  });

  btn.addEventListener('pointerup', (event) => {
    const state = active;
    active = null;
    if (!state || state.id !== event.pointerId) return;

    const dx = Math.abs(event.clientX - state.x);
    const dy = Math.abs(event.clientY - state.y);
    const dt = Date.now() - state.t;
    if (dx > TAP_MAX_MOVE_PX || dy > TAP_MAX_MOVE_PX) return;
    if (dt > TAP_MAX_DURATION_MS) return;

    // preventDefault on pointerup stops iOS from synthesizing a
    // trailing click event for the same gesture. We still guard with
    // the cooldown in case iOS ignores the preventDefault.
    event.preventDefault();
    tryFire();
  });

  btn.addEventListener('pointercancel', () => {
    active = null;
  });

  // Defensive fallback: if a `click` event somehow fires (mouse on
  // desktop without pointer events, or iOS overriding us), honor it
  // — but the cooldown above will drop it if a pointerup just fired.
  btn.addEventListener('click', (event) => {
    event.preventDefault();
    tryFire();
  });

  // Keyboard accessibility: Enter/Space on a focused button would
  // normally fire a synthetic click. Because we still listen to click
  // above, this mostly "just works", but we also handle keydown
  // directly so assistive tech / older browsers are covered.
  btn.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      tryFire();
    }
  });

  // Block the long-press context menu on Android Chrome (and any
  // right-click on desktop). iOS Safari's "tap and hold" callout is
  // already suppressed by the -webkit-touch-callout: none rule in
  // grid.css, but Android fires a regular `contextmenu` event that
  // isn't covered by that. Calling preventDefault here stops the
  // "Copy image / Open in new tab" menu from popping up while a
  // child is just holding the button.
  btn.addEventListener('contextmenu', (event) => {
    event.preventDefault();
  });
}
